import router from "@/router/";
import { resumeCameraSnapshots as resumeSnapshots } from "@/api/camera";
import { httpInstance as http } from "@/plugins/http";
import toast from "@/utils/toast";
import * as componentApi from "@/api/components";
import { components } from "@/store/tables/components";

import {
  SET_USERS,
  SET_IS_AUTHENTICATED,
  SET_USER_FULLNAME,
  SET_USER_PERMISSIONS,
  SET_COMPANIES,
  SET_CAMERAS,
  SET_PROJECTS,
  SET_SELECTED_CAMERA,
  UPDATE_CAMERA_SNAPSHOT_STATUS,
  SET_SELECTED_COMPANY,
  SET_SELECTED_PROJECT,
  SET_SELECTED_USER,
  SET_MODAL_STATE,
  SET_IS_LOADING_SYNC,
  SET_IS_SAVING,
  SET_PAGE_SIZE,
  SET_TOTAL_RECORDS,
  SET_MODAL_ACTIVE_TAB_INDEX,
  PUSH_MODAL,
  POP_MODAL,
  UPDATE_MODAL_TAB,
  CLOSE_ALL_MODALS,
  UPDATE_TOP_MODAL_DATA,
} from "@/store/mutations.js";

import { users } from "@/store/tables/users";
import { companies } from "@/store/tables/companies";
import { cameras } from "@/store/tables/cameras";
import { projects } from "@/store/tables/projects";
import { contracts } from "@/store/tables/contracts";
import { video_orders } from "@/store/tables/video_orders";
import { pro_camera_inventories } from "@/store/tables/pro_camera_inventories";

const tables = {
  components,
  users,
  companies,
  cameras,
  projects,
  contracts,
  video_orders,
  pro_camera_inventories,
};

let listRequestVersion = 0;

function resetState({ commit }) {
  commit(SET_IS_AUTHENTICATED, false);
  commit(SET_USER_FULLNAME, "");
  commit(SET_USERS, []);
  commit(SET_COMPANIES, []);
  commit(SET_CAMERAS, []);
  commit(SET_PROJECTS, []);

  commit(SET_MODAL_STATE, false);
  commit(SET_SELECTED_CAMERA, null);
  commit(SET_SELECTED_COMPANY, tables.companies.fields);
  commit(SET_SELECTED_PROJECT, null);
  commit(SET_SELECTED_USER, null);
  commit(SET_IS_LOADING_SYNC, false);

  sessionStorage.removeItem("jwt");
  sessionStorage.removeItem("refresh_jwt");
}
export const actions = {
  async loadComponentTypes({ commit }) {
    commit("SET_COMPONENT_TYPES_ERROR", "");
    try { const result = await componentApi.getComponentTypes(); commit("SET_COMPONENT_TYPES", result.data); }
    catch (error) { commit("SET_COMPONENT_TYPES", []); commit("SET_COMPONENT_TYPES_ERROR", error.message); }
  },
  openComponentModal({ commit }, data) { commit(PUSH_MODAL, { type: "component", data: data || {} }); },
  async openComponentCamera({ dispatch }, item) {
    try {
      const inventory = await componentApi.getInventory(item.inventory_id);
      dispatch("openProCameraInventoryModal", inventory);
    } catch (error) { toast.error(error.message); }
  },
  async saveComponent({ commit }, payload) { const result = await componentApi.saveComponent(payload); commit("COMPONENT_CHANGED"); return result; },
  async deleteComponent({ commit }, id) { await componentApi.deleteComponent(id); commit("COMPONENT_CHANGED"); },
  async installComponent({ commit }, payload) { await componentApi.installComponent(payload); commit("COMPONENT_CHANGED"); },
  async removeComponent({ commit }, payload) { await componentApi.removeComponent(payload); commit("COMPONENT_CHANGED"); },
  async resumeCameraSnapshots({ commit, dispatch, state, getters }, { cameraId }) {
    const status = await resumeSnapshots(cameraId);
    commit(UPDATE_CAMERA_SNAPSHOT_STATUS, { cameraId, status });
    if (state.filters.snapshotSuspended && getters.activeTab.table === "cameras") {
      dispatch("fetchList");
    }
    return status;
  },
  fetchList({ commit, state, getters }, { silent = false } = {}) {
    const requestVersion = ++listRequestVersion;
    let table = getters.activeTab;
    let tableName = table.table;
    const tabIndex = getters.activeTabIndex;
    let mutation = "SET_" + tableName.toUpperCase();
    if (tableName === "components") commit("SET_COMPONENT_LIST_ERROR", "");
    if (!silent) {
      commit(SET_IS_LOADING_SYNC, true);
      commit(mutation, []);
    }
    let params = {
      search: state.search ? state.search : "",
      page: table.currentPage,
      size: table.size,
    };

    if (typeof tables[tableName].getFetchParams === "function") {
      params = {
        ...tables[tableName].getFetchParams(state),
        ...params,
      };
    }

    const requestPath = (tables[tableName].path || tableName) + "/";
    return http
      .get(requestPath, {
        params,
      })
      .then((response) => {
        if (requestVersion !== listRequestVersion) return;
        if (!response || response.status > 299) throw new Error(response?.data?.message || "Could not load records");
        commit(mutation, response.data.data);
        commit(SET_TOTAL_RECORDS, {
          tabIndex,
          totalRecords: response.data.total_count,
          totalPages: response.data.last,
        });
        commit(SET_IS_LOADING_SYNC, false);
      }).catch((error) => {
        if (requestVersion === listRequestVersion && tableName === "components") commit("SET_COMPONENT_LIST_ERROR", error.message);
      }).finally(() => { if (requestVersion === listRequestVersion) commit(SET_IS_LOADING_SYNC, false); });
  },
  reset({ commit, getters }) {
    let table = tables[getters.activeTab.table];
    table.reset(commit);
  },
  save({ commit, getters, state, dispatch }) {
    let table = tables[getters.activeTab.table];

    if (!table.validate(state)) {
      toast.error("Please fill all required fields");
      return;
    }

    commit(SET_IS_SAVING, true);

    let method = "post";
    if (getters.activeTab.table === "contracts" && state.selectedContract.id) {
      method = "patch";
    }
    const url = `${tables[getters.activeTab.table].savePath || getters.activeTab.table}/`;

    http[method](url, table.getFormData(state))
      .then((response) => {
        table.saveCallBack(response, state, dispatch);
        toast.success("Saved successfully");
      })
      .catch((error) => {
        if (error.response.status === 409) {
          toast.error("This entity already exists.");
        } else {
          toast.error("Failed to save");
        }
      })
      .finally(() => {
        commit(SET_IS_SAVING, false);
      });
  },
  async login({ commit }, formData) {
    sessionStorage.removeItem("jwt");
    sessionStorage.removeItem("refresh_jwt");

    try {
      const response = await http.post("login/", formData);
      const data = response.data || {};

      const token =
        data.token || data.access_token || data.jwt || data.auth_token || "";
      const isAuthenticated = Object.prototype.hasOwnProperty.call(
        data,
        "isAuthenticated"
      )
        ? data.isAuthenticated
        : Boolean(token);

      if (!isAuthenticated) {
        resetState({ commit });
        throw new Error("Not authenticated");
      }

      if (token) {
        sessionStorage.setItem("jwt", token);
      }

      if (data.refresh_token) {
        sessionStorage.setItem("refresh_jwt", data.refresh_token);
      }

      commit(SET_IS_AUTHENTICATED, true);
      commit(
        SET_USER_FULLNAME,
        data.full_name || data.fullName || data.name || ""
      );
      commit(SET_USER_PERMISSIONS, data.permissions || []);

      return data; // Let caller handle navigation
    } catch (err) {
      resetState({ commit });
      const errorMessage =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err.message ||
        "Authentication failed";
      throw new Error(errorMessage);
    }
  },
  resetPassword({ commit }, email) {
    commit(SET_IS_AUTHENTICATED, false);
    return new Promise((resolve, reject) => {
      var formData = new FormData();
      formData.append("email", email);
      formData.append("op", "RESET_PASSWORD");

      http
        .post("user/", formData)
        .then((response) => {
          resolve(response);
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  logout({ commit }) {
    resetState({ commit });
    router.push("/");
  },
  backToLogin() {
    if (router.currentRoute.path === "/") {
      return;
    }
    router.push("/");
  },
  setPageSize({ commit }, payload) {
    commit(SET_PAGE_SIZE, {
      tabIndex: payload.tabIndex,
      pageSize: payload.pageSize,
    });
  },
  closeModal({ dispatch }) {
    // Deprecated - use closeTopModal instead
    dispatch("closeTopModal");
  },
  setActiveTab({ commit, dispatch }, index) {
    // Deprecated - use updateModalTab instead
    commit(SET_MODAL_ACTIVE_TAB_INDEX, index);
    dispatch("updateModalTab", index);
  },
  setSelectedUser({ commit }, user) {
    commit(SET_SELECTED_USER, user);
  },
  setModalState({ commit, dispatch }, state) {
    // Deprecated - use openUserModal/closeTopModal instead
    commit(SET_MODAL_STATE, state);
    if (!state) {
      dispatch("closeAllModals");
    }
  },
  editUser({ dispatch }, user) {
    // Deprecated - use openUserModal instead
    dispatch("openUserModal", user);
  },
  loginAsUser(_, user) {
    console.log(user);
    var formData = new FormData();
    formData.append("user_id", user.id);

    http.post("admin-login-token", formData).then((response) => {
      if (response.status !== 201) {
        toast.error("Error occurred");
        return;
      }

      window.open(
        `${process.env.VUE_APP_FE_HOST}?admin_login_token=${response.data.token}`
      );
    });
  },
  // Modal Stack Actions
  openUserModal({ commit }, user) {
    commit(PUSH_MODAL, {
      type: "user",
      data: user || tables.users.fields,
    });
  },
  openCompanyModal({ commit }, company) {
    commit(PUSH_MODAL, {
      type: "company",
      data: company || tables.companies.fields,
    });
  },
  openProjectModal({ commit }, project) {
    commit(PUSH_MODAL, {
      type: "project",
      data: project || tables.projects.fields,
    });
  },
  openCameraModal({ commit }, camera) {
    commit(PUSH_MODAL, {
      type: "camera",
      data: camera || tables.cameras.fields,
    });
  },
  openContractModal({ commit }, contract) {
    commit(PUSH_MODAL, {
      type: "contract",
      data: contract || tables.contracts.fields,
    });
  },
  openPaymentModal({ commit }, paymentData) {
    commit(PUSH_MODAL, {
      type: "payment",
      data: paymentData || {},
    });
  },
  openAddContractModal({ commit }, contractData) {
    commit(PUSH_MODAL, {
      type: "addContract",
      data: contractData || {},
    });
  },
  openEditContractModal({ commit }, contractData) {
    commit(PUSH_MODAL, {
      type: "editContract",
      data: contractData || {},
    });
  },
  openAddInvoiceModal({ commit }, invoiceData) {
    commit(PUSH_MODAL, {
      type: "addInvoice",
      data: invoiceData || {},
    });
  },
  openAddVideoOrderModal({ commit }) {
    commit(PUSH_MODAL, {
      type: "addVideoOrder",
      data: {},
    });
  },
  openEditInvoiceModal({ commit }, invoiceData) {
    commit(PUSH_MODAL, {
      type: "editInvoice",
      data: invoiceData || {},
    });
  },
  async queueVideoOrder({ dispatch }, order) {
    try {
      const response = await http.post(`video-orders/${order.id}/queue`);
      if (response.status === 200) {
        toast.success("Video order queued");
        dispatch("fetchList");
      }
    } catch {
      toast.error("Failed to queue video order");
    }
  },
  openProCameraInventoryModal({ commit }, inventory) {
    commit(PUSH_MODAL, {
      type: "proCameraInventory",
      data: inventory || tables.pro_camera_inventories.fields,
    });
  },
  openAddProCameraInventoryModal({ commit }) {
    commit(PUSH_MODAL, {
      type: "addProCameraInventory",
      data: {},
    });
  },
  async deleteProCameraInventory({ dispatch }, inventory) {
    try {
      await http.delete(`pro-camera/inventories/${inventory.id}`);
      toast.success("Inventory deleted");
      dispatch("fetchList");
    } catch {
      toast.error("Failed to delete inventory");
    }
  },
  closeTopModal({ commit, state }) {
    commit(POP_MODAL);
    // If no more modals, reset selected entities for backward compatibility
    if (state.modalStack.length === 0) {
      commit(SET_SELECTED_USER, null);
      commit(SET_SELECTED_COMPANY, tables.companies.fields);
      commit(SET_SELECTED_PROJECT, null);
      commit(SET_SELECTED_CAMERA, null);
    }
  },
  closeAllModals({ commit }) {
    commit(CLOSE_ALL_MODALS);
    // Reset selected entities for backward compatibility
    commit(SET_SELECTED_USER, null);
    commit(SET_SELECTED_COMPANY, tables.companies.fields);
    commit(SET_SELECTED_PROJECT, null);
    commit(SET_SELECTED_CAMERA, null);
  },
  updateModalTab({ commit, state }, tabIndex) {
    if (state.modalStack.length > 0) {
      const topModal = state.modalStack[state.modalStack.length - 1];
      commit(UPDATE_MODAL_TAB, { modalId: topModal.id, tabIndex });
    }
  },
  updateTopModalData({ commit }, data) {
    commit(UPDATE_TOP_MODAL_DATA, data);
  },
};
